import { useSelector, useDispatch } from 'react-redux'

// Actions
import { actions as userSongActions } from 'modules/usersongs/actions'

// Components
import { Button, Error, IconUpload, Image } from '@tracklib/kit'
import { Input, Form, Select, Checkbox, FileInput, ImageInput, baseFormValidation } from 'components/form'
import PageHeader from 'components/usersong/pageheader'
import SharingStatus from 'components/usersong/sharing/status'

// Hooks
import { useFeatureToggle } from 'modules/featuretoggles/hooks'
import { useRequest } from 'modules/requests/hooks'

// Types
import type { State, Beat, Dispatch } from 'types'

import styles from './index.css'

const visibilityOptions = [
  { id: 'public', label: 'Public', description: 'Share-able and displayed on your profile' },
  { id: 'unlisted', label: 'Unlisted', description: 'Share-able but not displayed on your profile' },
  { id: 'private', label: 'Private', description: 'Not visible on your profile or anywhere else' },
]

interface SharingProps {
  beatId: number
}

const Sharing = ({ beatId }: SharingProps): JSX.Element => {
  const dispatch = useDispatch() as Dispatch
  const beatSharingFeatureToggle = useFeatureToggle('beatSharing')
  const beat = useSelector<State, Beat>(state => state.entities.beats[beatId])
  const uploadAudioFileRequest = useRequest('UPLOAD_BEAT_AUDIO_FILE')

  const getFileInputMessage = () => {
    if (uploadAudioFileRequest.isLoading) {
      return 'Loading…'
    }
    if (uploadAudioFileRequest.hasError) {
      return 'No file uploaded'
    }
    return null
  }
  const handleUploadAudioFile = (fieldName: string, file: string, setFieldValue: (field: string, unknown: string) => void) => {
    if (file) {
      setFieldValue(fieldName, file)
      return dispatch(userSongActions.uploadBeatAudioFile(file, beat.id))
    }
    return null
  }

  if (!beatSharingFeatureToggle) {
    return <Error message="Page not found" />
  }
  return (
    <div className={styles.wrapper}>
      <PageHeader
        title="Sharing"
        subtitle="Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt. Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt."
      />
      <SharingStatus status={beat.visibility} beatId={beat.id} />
      <h3 className={styles.title}>Share beat on Tracklib</h3>
      <p className={styles.description}>
        Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim
        ad minim veniam, quis n. Lorem ipsum.
      </p>
      <Form
        onSubmit={updatedValues => dispatch(userSongActions.updateBeat(beatId, updatedValues))}
        initialValues={{
          title: beat.title,
          visibility: beat.visibility,
          terms_accepted: beat.terms_accepted,
          audio_file_name: beat.audio_file_name,
        }}
        validate={baseFormValidation({
          required: { fields: ['title', 'audio_file_name', 'terms_accepted'] },
        })}
        className={styles.wrapper}
        enableReinitialize
        formDoneDelay={2000}
      >
        {({ submitButtonProps, setFieldValue }) => (
          <div className={styles.stack}>
            <Input
              name="title"
              label="Beat title"
              description="You can choose a different name if you want to keep your real song name secret"
            />
            <FileInput
              name="audio_file_name"
              label="Audio file"
              onChange={(fieldName: string, file: string) => handleUploadAudioFile(fieldName, file, setFieldValue)}
              value={beat.audio_file_name}
              accept={['audio/mp3', 'audio/mpeg']}
              maxBytes={10485760}
              description="Your audio file must be in MP3 format, maximum 10mb."
              isLoading={uploadAudioFileRequest.isLoading}
              error={uploadAudioFileRequest.hasError && 'Ouch! Something went wrong, please try again…'}
              message={getFileInputMessage()}
            >
              <IconUpload /> Upload audio file
            </FileInput>
            <ImageInput
              label="Cover art"
              optional
              onChange={setFieldValue}
              name="cover"
              currentImage={beat.cover ? <Image image={beat.cover} alt="Beat cover art" /> : <span className={styles.fallbackCover} />}
            />
            <Select name="visibility" custom label="Visibility" className={styles.visibilitySelect}>
              {visibilityOptions.map(({ label, id, description }) => (
                <div key={id} value={id} className={styles.visibilityOption}>
                  <p className={styles.visibilityTitle}>{label}</p>
                  <p className={styles.visibilityDescription}>{description}</p>
                </div>
              ))}
            </Select>
            <Checkbox name="terms_accepted" size="large" type="yellow" bordered>
              <b>I understand that my beat</b> will be/not be visible to other users and that I can not share this on instagram or anywhere
              else than Tracklib.
            </Checkbox>
            <Button {...submitButtonProps}>Save and publish</Button>
          </div>
        )}
      </Form>
    </div>
  )
}

export default Sharing
